import {
  CATEGORIES,
  type Category,
  COUNTED_WAYS,
  countedWays,
  DISPLAY_UNITS,
  type Dimension,
  defaultsFor,
  formatQuantity,
  HOP_FORM_LABELS,
  HOP_FORMS,
  type HopForm,
  isDecimalString,
  type SpecProperty,
  type UnitId,
  YEAST_FORM_DEFAULTS,
  YEAST_FORMS,
  type YeastForm,
} from "@bl/domain";
import { Link, useNavigate } from "@tanstack/react-router";
import { BookOpen, FileUp, Plus, Search } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  type CatalogProduct,
  type Item,
  type Lot,
  type LotPatch,
  send,
  useCatalog,
  useCatalogBrowse,
  useChange,
  useItems,
  useSuppliers,
} from "../api";
import {
  CategoryChip,
  Dialog,
  DialogForm,
  ErrorNote,
  Field,
  PriceInput,
  type QuantityUnit,
} from "../components/ui";
import { categoryLabel, mainSourceOf, showDate, unitLabel } from "../format";
import { useNotify } from "../notify";
import { BrandInput } from "./BrandInput";
import { type PackChoice, PackPicker, packChoiceOf, usablePack } from "./PackPicker";
import { RegionInput } from "./RegionInput";
import {
  collectSpecs,
  makeDrafts,
  type SpecChanges,
  SpecFields,
  useSpecDrafts,
} from "./SpecFields";

const MEASURES: { dimension: Dimension; label: string }[] = [
  { dimension: "mass", label: "Weight" },
  { dimension: "volume", label: "Volume" },
  { dimension: "count", label: "Count" },
];

/* ---------- Add item ---------- */

/**
 * `category` rides along from `browse` through `product` and back. It used to live inside
 * CatalogBrowse, which the product view unmounts, so filtering to yeast and then looking at one
 * yeast dropped you back into the unfiltered list on Back (Tyler, 2026-09-23). Where the brewer had
 * got to is the dialog's business, not a child's.
 */
type AddStep =
  | { step: "choose" }
  | { step: "search"; query: string }
  | { step: "browse"; query: string; category: Category | null }
  | {
      step: "product";
      query: string;
      product: CatalogProduct;
      from: "search" | "browse";
      category: Category | null;
    }
  | { step: "manual"; query: string };

const ADD_TITLES: Record<AddStep["step"], string> = {
  choose: "Add an item",
  search: "Find the item",
  browse: "Catalog",
  product: "Add to inventory",
  manual: "Create an item",
};

/**
 * Adding stock to a brewery's inventory. The shared catalog is never shown as a list (Tyler,
 * 2026-09-19): a brewery searches it by name or SKU, and only what it adds becomes visible to it.
 */
export function AddItemDialog({
  open,
  onClose,
  onAdded,
  initialQuery,
  submitLabel,
  offerInvoice = true,
  soldBy,
  soldIn,
}: {
  open: boolean;
  onClose: () => void;
  /**
   * Given, the new item is handed back and the dialog closes where it stands. Opened from an invoice
   * line, going off to the item's page would lose the brewer's place in the review (Tyler,
   * 2026-09-20).
   */
  onAdded?: (item: Item) => void;
  initialQuery?: string;
  /** What the last button says. From an invoice, adding an item is adding it to inventory. */
  submitLabel?: string;
  /** False where an invoice is already what the person is doing, which would be circular. */
  offerInvoice?: boolean;
  /**
   * How the invoice line it was opened from sells it, where that can be read: CMG's "1GAL" is sold
   * by volume, so a fining added from it starts counted by volume (2026-10-01).
   */
  soldBy?: Dimension | null;
  /** The unit the invoice sells it in ("mL" for a 250 mL bottle), to show it in. */
  soldIn?: UnitId | null;
}) {
  // With nothing to choose between, the choice is skipped and the search opens on the line's own name.
  const start = (): AddStep =>
    offerInvoice ? { step: "choose" } : { step: "search", query: initialQuery ?? "" };
  const [state, setState] = useState<AddStep>(start);
  const navigate = useNavigate();
  const close = () => {
    setState(start());
    onClose();
  };
  const added = (item: Item) => {
    close();
    if (onAdded) onAdded(item);
    else navigate({ to: "/items/$itemId", params: { itemId: item.id } });
  };
  return (
    <Dialog
      open={open}
      onClose={close}
      title={ADD_TITLES[state.step]}
      wide={state.step !== "choose"}
    >
      {state.step === "choose" ? (
        <ChooseHow
          onManual={() => setState({ step: "search", query: initialQuery ?? "" })}
          onCancel={close}
        />
      ) : null}
      {state.step === "search" ? (
        <CatalogSearch
          initialQuery={state.query}
          onPick={(product, query) =>
            setState({ step: "product", query, product, from: "search", category: null })
          }
          onBrowse={(query) => setState({ step: "browse", query, category: null })}
          onCreate={(query) => setState({ step: "manual", query })}
          // With no choice offered there is none to go back to: it offers Enter an invoice, which
          // leaves whatever the brewer opened this from.
          onBack={offerInvoice ? () => setState({ step: "choose" }) : close}
        />
      ) : null}
      {state.step === "browse" ? (
        <CatalogBrowse
          category={state.category}
          onCategory={(category) => setState({ ...state, category })}
          onPick={(product) =>
            setState({
              step: "product",
              query: state.query,
              product,
              from: "browse",
              category: state.category,
            })
          }
          onCreate={() => setState({ step: "manual", query: state.query })}
          onBack={() => setState({ step: "search", query: state.query })}
        />
      ) : null}
      {state.step === "product" ? (
        <AddFromCatalog
          product={state.product}
          soldBy={soldBy}
          soldIn={soldIn}
          onAdded={added}
          onBack={() =>
            setState(
              state.from === "browse"
                ? { step: "browse", query: state.query, category: state.category }
                : { step: "search", query: state.query },
            )
          }
        />
      ) : null}
      {state.step === "manual" ? (
        <NewItemForm
          initialName={state.query}
          onAdded={added}
          submitLabel={submitLabel}
          onBack={() => setState({ step: "search", query: state.query })}
        />
      ) : null}
    </Dialog>
  );
}

function ChooseHow({ onManual, onCancel }: { onManual: () => void; onCancel: () => void }) {
  return (
    <DialogForm
      onSubmit={onManual}
      footer={
        <button type="button" className="button" onClick={onCancel}>
          Cancel
        </button>
      }
    >
      <div className="choice-grid">
        <Link to="/invoices" className="choice" onClick={onCancel}>
          <FileUp size={26} />
          <strong>Enter an invoice</strong>
          <span>Everything on one delivery at once, with the document kept beside it.</span>
        </Link>
        <button
          type="button"
          className="choice"
          onClick={onManual}
          // biome-ignore lint/a11y/noAutofocus: the one available choice
          autoFocus
        >
          <Search size={26} />
          <strong>Add an item manually</strong>
          <span>Search by name or SKU, then add it.</span>
        </button>
      </div>
    </DialogForm>
  );
}

function CatalogSearch({
  initialQuery,
  onPick,
  onBrowse,
  onCreate,
  onBack,
}: {
  initialQuery: string;
  onPick: (product: CatalogProduct, query: string) => void;
  onBrowse: (query: string) => void;
  onCreate: (query: string) => void;
  onBack: () => void;
}) {
  const [query, setQuery] = useState(initialQuery);
  const [typed, setTyped] = useState(initialQuery);
  useEffect(() => {
    const t = setTimeout(() => setQuery(typed), 150);
    return () => clearTimeout(t);
  }, [typed]);
  const { data: products = [], isFetching, isError } = useCatalog(query, null);
  const searching = query.trim().length >= 2;
  const first = products[0];
  return (
    <DialogForm
      onSubmit={() => first && onPick(first, typed)}
      footer={
        <>
          <button type="button" className="button" onClick={onBack} style={{ marginRight: "auto" }}>
            Back
          </button>
          <button type="button" className="button" onClick={() => onBrowse(typed.trim())}>
            <BookOpen size={16} /> View catalog
          </button>
          <button type="button" className="button" onClick={() => onCreate(typed.trim())}>
            <Plus size={16} /> Create it yourself
          </button>
        </>
      }
    >
      <div className="search-field">
        <Search size={18} aria-hidden />
        <input
          className="input"
          type="search"
          placeholder="Start typing a name or SKU"
          aria-label="Search by name or SKU"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          // biome-ignore lint/a11y/noAutofocus: the dialog step is the search
          autoFocus
        />
      </div>
      {!searching ? (
        <p className="muted">
          Type at least two letters: a malt, a hop, a maker, or the SKU from an invoice.
        </p>
      ) : null}
      {isError ? <p className="form-error">Search failed. Try again.</p> : null}
      {searching && !isFetching && products.length === 0 ? (
        <p className="muted">
          Nothing matches &ldquo;{query.trim()}&rdquo;. Use <strong>Create it yourself</strong> to
          add it by hand.
        </p>
      ) : null}
      {searching && products.length > 0 ? (
        <ul className="suggestions" aria-label="Suggestions" aria-busy={isFetching}>
          {products.map((p) => (
            <ProductRow key={p.id} product={p} onPick={() => onPick(p, typed)} />
          ))}
        </ul>
      ) : null}
    </DialogForm>
  );
}

/** Who made it, or for a hop as grown its form; and where it comes from. */
function productLine(p: CatalogProduct): string {
  const hop = p.hop ? HOP_FORM_LABELS[p.hop.form] + (p.hop.blend ? " blend" : "") : null;
  return [p.manufacturer, hop, p.origin].filter(Boolean).join(" · ");
}

/** A supplier's item number, and the hop company on it where that is someone else. */
function offeringLine(o: CatalogProduct["offerings"][number]): string {
  const brand = o.brand && o.brand.name !== o.supplier.name ? `(${o.brand.name})` : null;
  return [o.supplier.name, o.sku, brand].filter(Boolean).join(" ");
}

/** One catalog product as a pickable row, shared by search and the full catalog view. */
function ProductRow({ product: p, onPick }: { product: CatalogProduct; onPick: () => void }) {
  return (
    <li>
      <button type="button" onClick={onPick}>
        <span className="suggestion-main">
          <strong>{p.name}</strong>
          <CategoryChip category={p.category} />
          {p.adoptedItemIds.length ? <span className="chip ok plain">In inventory</span> : null}
        </span>
        <span className="muted">{productLine(p)}</span>
        <span className="faint">{p.offerings.map(offeringLine).join("; ")}</span>
      </button>
    </li>
  );
}

/** The whole catalog, only when chosen: grouped by category, a page at a time. */
function CatalogBrowse({
  category,
  onCategory,
  onPick,
  onCreate,
  onBack,
}: {
  category: Category | null;
  onCategory: (category: Category | null) => void;
  onPick: (product: CatalogProduct) => void;
  onCreate: () => void;
  onBack: () => void;
}) {
  const { data, isLoading, isError, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useCatalogBrowse(category);
  const products = data?.pages.flatMap((page) => page.products) ?? [];
  const groups = CATEGORIES.map((c) => ({
    category: c,
    items: products.filter((p) => p.category === c),
  })).filter((g) => g.items.length > 0);
  return (
    <DialogForm
      onSubmit={() => {}}
      footer={
        <>
          <button type="button" className="button" onClick={onBack} style={{ marginRight: "auto" }}>
            Back to search
          </button>
          <button type="button" className="button" onClick={onCreate}>
            <Plus size={16} /> Create it yourself
          </button>
        </>
      }
    >
      <select
        className="select"
        value={category ?? ""}
        aria-label="Category"
        onChange={(e) => onCategory((e.target.value || null) as Category | null)}
      >
        <option value="">All categories</option>
        {CATEGORIES.map((c) => (
          <option key={c} value={c}>
            {categoryLabel(c)}
          </option>
        ))}
      </select>
      {isLoading ? <p className="muted">Loading the catalog</p> : null}
      {isError ? <p className="form-error">Could not load the catalog.</p> : null}
      {!isLoading && products.length === 0 ? (
        <p className="muted">Nothing in the catalog here yet.</p>
      ) : null}
      {groups.map((g) => (
        <div key={g.category} className="browse-group">
          <h3 className="group-heading">
            <CategoryChip category={g.category} />
            <span className="count">{g.items.length}</span>
          </h3>
          <ul className="suggestions">
            {g.items.map((p) => (
              <ProductRow key={p.id} product={p} onPick={() => onPick(p)} />
            ))}
          </ul>
        </div>
      ))}
      {hasNextPage ? (
        <div>
          <button
            type="button"
            className="button"
            disabled={isFetchingNextPage}
            onClick={() => fetchNextPage()}
          >
            {isFetchingNextPage ? "Loading" : "Show more"}
          </button>
        </div>
      ) : null}
    </DialogForm>
  );
}

const COUNTED_BY: Record<Dimension, string> = {
  mass: "Weight (lb, oz, kg, g)",
  volume: "Volume (gal, L, fl oz)",
  count: "Count (each)",
  pitch: "Pitches",
};

function AddFromCatalog({
  product,
  soldBy,
  soldIn,
  onAdded,
  onBack,
}: {
  product: CatalogProduct;
  soldBy?: Dimension | null;
  soldIn?: UnitId | null;
  onAdded: (item: Item) => void;
  onBack: () => void;
}) {
  const notify = useNotify();
  const offerings = product.offerings;
  const [offeringId, setOfferingId] = useState(offerings[0]?.id ?? "");
  const [yeastForm, setYeastForm] = useState<YeastForm>("liquid");
  // A chemical, a sugar or an adjunct can be a powder or a liquid; the catalog does not say which.
  const ways = countedWays(product.category);
  const [dimension, setDimension] = useState<Dimension>(
    soldBy && ways.includes(soldBy) ? soldBy : (ways[0] as Dimension),
  );
  // Shown in the unit it is sold in where that measures this way: Propper Phantasm's 250 mL bottle in
  // mL, not gallons (Scott's Jam, 2026-10-07). Otherwise the kind's usual unit, or the first.
  const shownIn = (d: Dimension): UnitId =>
    soldIn && DISPLAY_UNITS[d].includes(soldIn)
      ? soldIn
      : d === defaultsFor(product.category).dimension
        ? defaultsFor(product.category).displayUnit
        : (DISPLAY_UNITS[d][0] as UnitId);
  const [displayUnit, setDisplayUnit] = useState<UnitId>(() => shownIn(dimension));
  const { drafts, set: setDraft } = useSpecDrafts(product.specs, product.category);
  const mainSource = mainSourceOf(product.specs);
  const [specError, setSpecError] = useState<string | null>(null);
  const change = useChange(
    (_: undefined, key) => {
      const changes = collectSpecs(drafts);
      if (changes.error) throw new Error(changes.error);
      return send.item(
        {
          source: "catalog",
          catalogProductId: product.id,
          offeringId: offeringId || undefined,
          yeastForm: product.category === "yeast" && !product.yeastForm ? yeastForm : undefined,
          dimension: ways.length > 1 ? dimension : undefined,
          displayUnit: ways.length > 1 ? displayUnit : undefined,
          specs: changes.specs.length ? changes.specs : undefined,
        },
        key,
      );
    },
    {
      onSuccess: (item) => {
        notify({
          title: `${item.displayName} added to your inventory`,
          link: { kind: "item", itemId: item.id },
        });
        onAdded(item);
      },
    },
  );
  const existing = product.adoptedItemIds[0];
  const asOf = [
    ...product.specs.map((s) => s.source?.asOf),
    ...product.offerings.map((o) => o.source.asOf),
  ]
    .filter(Boolean)
    .sort()
    .at(-1);
  return (
    <DialogForm
      onSubmit={() => {
        const changes = collectSpecs(drafts);
        setSpecError(changes.error);
        if (!existing && !changes.error) change.mutate(undefined);
      }}
      footer={
        <>
          <button type="button" className="button" onClick={onBack} style={{ marginRight: "auto" }}>
            Back
          </button>
          {existing ? (
            <Link to="/items/$itemId" params={{ itemId: existing }} className="button primary">
              Open it
            </Link>
          ) : (
            <button type="submit" className="button primary" disabled={change.isPending}>
              <Plus size={16} /> Add to inventory
            </button>
          )}
        </>
      }
    >
      <div>
        <div className="row">
          <h3 style={{ margin: 0, fontSize: 18 }}>{product.name}</h3>
          <CategoryChip category={product.category} />
        </div>
        <div className="muted">{productLine(product)}</div>
        {product.description ? <p style={{ marginBottom: 0 }}>{product.description}</p> : null}
      </div>
      {existing ? <div className="notice">This is already in your inventory.</div> : null}
      {drafts.length ? (
        <>
          <p className="muted" style={{ margin: 0 }}>
            {mainSource ? `Source: ${mainSource}. ` : ""}Modify to set values for your inventory.
          </p>
          <SpecFields drafts={drafts} onChange={setDraft} mainSource={mainSource} />
        </>
      ) : null}
      {offerings.length > 1 ? (
        <fieldset className="offer-choice">
          <legend>Usually bought as</legend>
          {offerings.map((o) => (
            <label key={o.id} className="check-row">
              <input
                type="radio"
                name="offering"
                checked={offeringId === o.id}
                onChange={() => setOfferingId(o.id)}
              />
              {offeringLine(o)}
            </label>
          ))}
        </fieldset>
      ) : offerings[0] ? (
        <p className="muted" style={{ margin: 0 }}>
          Sold by {offerings[0].supplier.name}
          {offerings[0].sku ? ` as ${offerings[0].sku}` : ""}
          {offerings[0].brand && offerings[0].brand.name !== offerings[0].supplier.name
            ? ` (${offerings[0].brand.name})`
            : ""}
          .
        </p>
      ) : null}
      {ways.length > 1 ? (
        <Field
          label="Counted by"
          hint="As it is sold: a liquid by volume, a powder by weight. It cannot change once there is stock."
        >
          <select
            className="select"
            value={dimension}
            onChange={(e) => {
              const d = e.target.value as Dimension;
              setDimension(d);
              setDisplayUnit(shownIn(d));
            }}
          >
            {ways.map((w) => (
              <option key={w} value={w}>
                {COUNTED_BY[w]}
              </option>
            ))}
          </select>
        </Field>
      ) : null}
      {ways.length > 1 && DISPLAY_UNITS[dimension].length > 1 ? (
        <Field
          label="Shown in"
          hint="The unit its stock and recipes read in. You can change it later."
        >
          <select
            className="select"
            value={displayUnit}
            onChange={(e) => setDisplayUnit(e.target.value as UnitId)}
          >
            {DISPLAY_UNITS[dimension].map((u) => (
              <option key={u} value={u}>
                {unitLabel(u)}
              </option>
            ))}
          </select>
        </Field>
      ) : null}
      {product.category === "yeast" && !product.yeastForm ? (
        <Field label="Form">
          <select
            className="select"
            value={yeastForm}
            onChange={(e) => setYeastForm(e.target.value as YeastForm)}
          >
            {YEAST_FORMS.map((f) => (
              <option key={f} value={f}>
                {YEAST_FORM_DEFAULTS[f].label}
              </option>
            ))}
          </select>
        </Field>
      ) : null}
      {asOf ? (
        <p className="faint" style={{ margin: 0 }}>
          Details as of {showDate(asOf)}. Adding copies them into your inventory.
        </p>
      ) : null}
      {specError ? <div className="form-error">{specError}</div> : null}
      <ErrorNote error={change.error} />
    </DialogForm>
  );
}

/**
 * What is asked of an item made by hand, where no catalog fills anything in: the figures a recipe line
 * starts from, and for a yeast what tells one strain from another. Omega's strains are in none of the
 * three suppliers' catalogs, and Scott wanted to type one in once and pick it from then on (Scott's
 * Jam, 2026-10-02).
 */
const BY_HAND: Partial<Record<Category, SpecProperty[]>> = {
  yeast: ["lab_code", "attenuation", "fermentation_temp", "flocculation", "alcohol_tolerance"],
  hop: ["alpha_acid", "beta_acid", "total_oil"],
  malt: ["color", "extract_fine"],
  sugar: ["color", "extract_fine"],
  adjunct: ["color", "extract_fine"],
};

/**
 * A new item typed in by hand, saved to inventory so it can be picked again. Shared by Add item's
 * "Create it yourself" and the recipe builder's ingredient search, which offers only the categories
 * its tab can use.
 */
export function NewItemForm({
  initialName,
  categories = CATEGORIES,
  onAdded,
  submitLabel,
  onBack,
}: {
  initialName: string;
  /** The categories it can be, the first to start with. */
  categories?: readonly Category[];
  onAdded: (item: Item) => void;
  submitLabel?: string;
  onBack: () => void;
}) {
  const notify = useNotify();
  const [name, setName] = useState(initialName);
  const [category, setCategory] = useState<Category>(categories[0] ?? "malt");
  const [yeastForm, setYeastForm] = useState<YeastForm>("dry");
  const [hopForm, setHopForm] = useState<HopForm | "">("");
  const defaults = defaultsFor(category, category === "yeast" ? yeastForm : null);
  const [dimension, setDimension] = useState<Dimension>(defaults.dimension);
  const [displayUnit, setDisplayUnit] = useState<UnitId>(defaults.displayUnit);
  // No pack until someone picks one: the first receipt sets it anyway, from what actually came.
  const [pack, setPack] = useState<PackChoice>(null);
  const [manufacturer, setManufacturer] = useState("");
  const [origin, setOrigin] = useState("");
  const [description, setDescription] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [supplierSku, setSupplierSku] = useState("");
  // Kept by property, so a figure typed before the category changes is still there if it applies.
  const [figures, setFigures] = useState<Record<string, string>>({});
  const [specError, setSpecError] = useState<string | null>(null);
  const { data: suppliers = [] } = useSuppliers();
  const drafts = makeDrafts([], category, BY_HAND[category] ?? []).map((d) => ({
    ...d,
    text: figures[d.property] ?? "",
  }));

  function chooseCategory(c: Category, form = yeastForm) {
    setCategory(c);
    const d = defaultsFor(c, c === "yeast" ? form : null);
    setDimension(d.dimension);
    setDisplayUnit(d.displayUnit);
    setPack(packChoiceOf(d.pack, d.dimension));
  }

  const change = useChange(
    (specs: SpecChanges["specs"], key) =>
      send.item(
        {
          source: "manual",
          name: name.trim(),
          category,
          yeastForm: category === "yeast" ? yeastForm : undefined,
          hopForm: category === "hop" && hopForm ? hopForm : undefined,
          dimension: category === "yeast" ? undefined : dimension,
          displayUnit,
          pack: usablePack(pack),
          manufacturer: manufacturer.trim() || undefined,
          // A hop's region is each lot's, asked when it comes in.
          origin: category !== "hop" ? origin.trim() || undefined : undefined,
          description: description.trim() || undefined,
          defaultSupplierId: supplierId || undefined,
          defaultSupplierSku: supplierSku.trim() || undefined,
          specs: specs.length ? specs : undefined,
        },
        key,
      ),
    {
      onSuccess: (item) => {
        notify({ title: `${item.displayName} added`, link: { kind: "item", itemId: item.id } });
        onAdded(item);
      },
    },
  );

  return (
    <DialogForm
      onSubmit={() => {
        if (!name.trim()) return;
        const changes = collectSpecs(drafts);
        setSpecError(changes.error);
        if (!changes.error) change.mutate(changes.specs);
      }}
      footer={
        <>
          <button type="button" className="button" onClick={onBack} style={{ marginRight: "auto" }}>
            Back
          </button>
          <button
            type="submit"
            className="button primary"
            disabled={!name.trim() || change.isPending}
          >
            {submitLabel ?? "Add item"}
          </button>
        </>
      }
    >
      <div className="form-grid">
        <Field label="Name" required className="wide">
          <input
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            // biome-ignore lint/a11y/noAutofocus: first field of the form
            autoFocus
            required
          />
        </Field>
        {categories.length > 1 ? (
          <Field label="Category">
            <select
              className="select"
              value={category}
              onChange={(e) => chooseCategory(e.target.value as Category)}
            >
              {categories.map((c) => (
                <option key={c} value={c}>
                  {categoryLabel(c)}
                </option>
              ))}
            </select>
          </Field>
        ) : null}
        <Field label={category === "yeast" ? "Lab" : "Maker"}>
          <input
            className="input"
            value={manufacturer}
            onChange={(e) => setManufacturer(e.target.value)}
          />
        </Field>
        {category === "hop" ? <HopFormField value={hopForm} onChange={setHopForm} /> : null}
        {category === "yeast" ? (
          <Field label="Form" hint="The form decides how it is measured">
            <select
              className="select"
              value={yeastForm}
              onChange={(e) => {
                const f = e.target.value as YeastForm;
                setYeastForm(f);
                chooseCategory("yeast", f);
              }}
            >
              {YEAST_FORMS.map((f) => (
                <option key={f} value={f}>
                  {YEAST_FORM_DEFAULTS[f].label}
                </option>
              ))}
            </select>
          </Field>
        ) : (
          <Field label="Measured by" hint="Fixed once the item exists">
            <select
              className="select"
              value={dimension}
              onChange={(e) => {
                const d = e.target.value as Dimension;
                setDimension(d);
                setDisplayUnit(DISPLAY_UNITS[d][0] as UnitId);
              }}
            >
              {MEASURES.map((m) => (
                <option key={m.dimension} value={m.dimension}>
                  {m.label}
                </option>
              ))}
            </select>
          </Field>
        )}
      </div>
      {drafts.length ? (
        <>
          <p className="muted" style={{ margin: 0 }}>
            As the {category === "yeast" ? "lab" : "maker"} prints them: a number, a range (73 - 77)
            or a limit (&lt; 4.5). Leave any you don&rsquo;t know.
          </p>
          <SpecFields
            drafts={drafts}
            onChange={(property, text) => setFigures((f) => ({ ...f, [property]: text }))}
            markYours={false}
          />
        </>
      ) : null}
      <div className="form-grid">
        <Field label="Show amounts in">
          <select
            className="select"
            value={displayUnit}
            onChange={(e) => setDisplayUnit(e.target.value as UnitId)}
          >
            {DISPLAY_UNITS[dimension].map((u) => (
              <option key={u} value={u}>
                {unitLabel(u)}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Comes in" hint="Set by each delivery from then on">
          <PackPicker
            // A new category is a new list: start the picker over rather than carry a stale choice.
            key={`${category}-${dimension}`}
            dimension={dimension}
            category={category}
            displayUnit={displayUnit}
            value={pack}
            onChange={setPack}
          />
        </Field>
        <Field label="Usually bought from">
          <select
            className="select"
            value={supplierId}
            onChange={(e) => setSupplierId(e.target.value)}
          >
            <option value="">Nobody in particular</option>
            {suppliers.map((sp) => (
              <option key={sp.id} value={sp.id}>
                {sp.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Their SKU">
          <input
            className="input"
            value={supplierSku}
            onChange={(e) => setSupplierSku(e.target.value)}
          />
        </Field>
        {category !== "hop" ? (
          <Field label="Origin">
            <input className="input" value={origin} onChange={(e) => setOrigin(e.target.value)} />
          </Field>
        ) : null}
        <Field label="Description" className="wide">
          <textarea
            className="textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Field>
      </div>
      {specError ? <div className="form-error">{specError}</div> : null}
      <ErrorNote error={change.error} />
    </DialogForm>
  );
}

/**
 * A hop's form, kept beside its variety rather than in its name (Scott's Jam, 2026-10-02): "Citra",
 * in T-90 pellets.
 */
function HopFormField({
  value,
  onChange,
}: {
  value: HopForm | "";
  onChange: (form: HopForm | "") => void;
}) {
  return (
    <Field label="Form" hint="Shown beside the name">
      <select
        className="select"
        value={value}
        onChange={(e) => onChange(e.target.value as HopForm | "")}
      >
        <option value="">Not given</option>
        {HOP_FORMS.map((f) => (
          <option key={f} value={f}>
            {HOP_FORM_LABELS[f]}
          </option>
        ))}
      </select>
    </Field>
  );
}

/* ---------- Edit item ---------- */

export function EditItemDialog({
  open,
  onClose,
  item,
}: {
  open: boolean;
  onClose: () => void;
  item: Item;
}) {
  return (
    <Dialog open={open} onClose={onClose} title={`Edit ${item.displayName}`}>
      {open ? <EditItemForm item={item} onDone={onClose} /> : null}
    </Dialog>
  );
}

function EditItemForm({ item, onDone }: { item: Item; onDone: () => void }) {
  const notify = useNotify();
  const dimension = item.dimension as Dimension;
  // The name as it reads, maker's short name and all, so what is edited is what shows. Changed, it is
  // the brewery's own and reads exactly as typed from then on (Scott's Jam, 2026-10-08).
  const [name, setName] = useState(item.displayName);
  // Another kind counted as this one is (Tyler, 2026-10-03: a fruit concentrate filed as a brewing
  // aid); a hop or a yeast keeps its kind, which its form belongs to.
  const [category, setCategory] = useState<Category>(item.category as Category);
  const kinds =
    item.category === "hop" || item.category === "yeast"
      ? []
      : CATEGORIES.filter(
          (c) => c !== "hop" && c !== "yeast" && COUNTED_WAYS[c].includes(dimension),
        );
  // A hop's form is its catalog product's once it is linked; one added by hand says its own.
  const formSettable = item.category === "hop" && !item.catalogProductId;
  const [hopForm, setHopForm] = useState<HopForm | "">(item.hopForm ?? "");
  const [displayUnit, setDisplayUnit] = useState<UnitId>(item.displayUnit);
  // An item's pack is what it last came in, and each delivery sets it. Before the first one there is
  // nothing to go on, so it can be said here; after that the deliveries decide (Tyler, 2026-09-23).
  const packSettable = item.historyCount === 0;
  const [pack, setPack] = useState<PackChoice>(packChoiceOf(item.pack, dimension));
  const [manufacturer, setManufacturer] = useState(item.manufacturer ?? "");
  const [origin, setOrigin] = useState(item.origin ?? "");
  const [description, setDescription] = useState(item.description ?? "");
  const [supplierId, setSupplierId] = useState(item.defaultSupplier?.id ?? "");
  const [supplierSku, setSupplierSku] = useState(item.defaultSupplierSku ?? "");
  const { data: suppliers = [] } = useSuppliers();

  const change = useChange(
    (archived: boolean | undefined, key) =>
      send.patchItem(
        {
          id: item.id,
          patch:
            archived !== undefined
              ? { archived }
              : {
                  name: name.trim(),
                  ...(category !== item.category ? { category } : {}),
                  displayUnit,
                  ...(formSettable ? { hopForm: hopForm || null } : {}),
                  ...(packSettable ? { pack: usablePack(pack) } : {}),
                  manufacturer: manufacturer.trim() || null,
                  ...(item.category !== "hop" ? { origin: origin.trim() || null } : {}),
                  description: description.trim() || null,
                  defaultSupplierId: supplierId || null,
                  defaultSupplierSku: supplierSku.trim() || null,
                },
        },
        key,
      ),
    {
      onSuccess: (updated) => {
        notify({
          title: updated.archived
            ? `${updated.displayName} archived`
            : `${updated.displayName} saved`,
          link: { kind: "item", itemId: updated.id },
        });
        onDone();
      },
    },
  );

  return (
    <DialogForm
      onSubmit={() => change.mutate(undefined)}
      footer={
        <>
          <button
            type="button"
            className="button danger"
            onClick={() => change.mutate(!item.archived)}
            style={{ marginRight: "auto" }}
          >
            {item.archived ? "Unarchive" : "Archive"}
          </button>
          <button type="button" className="button" onClick={onDone}>
            Cancel
          </button>
          <button type="submit" className="button primary" disabled={change.isPending}>
            Save
          </button>
        </>
      }
    >
      <div className="form-grid">
        <Field label="Name" required className="wide">
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        {kinds.length > 1 ? (
          <Field label="Category">
            <select
              className="select"
              value={category}
              onChange={(e) => setCategory(e.target.value as Category)}
            >
              {kinds.map((c) => (
                <option key={c} value={c}>
                  {categoryLabel(c)}
                </option>
              ))}
            </select>
          </Field>
        ) : null}
        {formSettable ? <HopFormField value={hopForm} onChange={setHopForm} /> : null}
        <Field label="Show amounts in" hint={`Measured by ${dimension}; that cannot change`}>
          <select
            className="select"
            value={displayUnit}
            onChange={(e) => setDisplayUnit(e.target.value as UnitId)}
          >
            {DISPLAY_UNITS[dimension].map((u) => (
              <option key={u} value={u}>
                {unitLabel(u)}
              </option>
            ))}
          </select>
        </Field>
        {packSettable ? (
          <Field label="Comes in" hint="Until the first delivery says otherwise">
            <PackPicker
              dimension={dimension}
              category={item.category as Category}
              displayUnit={displayUnit}
              value={pack}
              onChange={setPack}
            />
          </Field>
        ) : null}
        <Field label="Usually bought from" hint="Prefills Receive; each receipt updates it">
          <select
            className="select"
            value={supplierId}
            onChange={(e) => setSupplierId(e.target.value)}
          >
            <option value="">Nobody in particular</option>
            {suppliers.map((sp) => (
              <option key={sp.id} value={sp.id}>
                {sp.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Their SKU">
          <input
            className="input"
            value={supplierSku}
            onChange={(e) => setSupplierSku(e.target.value)}
          />
        </Field>
        <Field label="Maker">
          <input
            className="input"
            value={manufacturer}
            onChange={(e) => setManufacturer(e.target.value)}
          />
        </Field>
        {item.category !== "hop" ? (
          <Field label="Origin">
            <input className="input" value={origin} onChange={(e) => setOrigin(e.target.value)} />
          </Field>
        ) : null}
        <Field label="Description" className="wide">
          <textarea
            className="textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Field>
      </div>
      <ErrorNote error={change.error} />
    </DialogForm>
  );
}

/* ---------- Link to the catalog ---------- */

/**
 * An item made by hand, linked to the catalog product it is (Scott's Jam, 2026-10-02): Ollie's
 * "Canada Malting 2 Row" fitted no product's name, so it came in with no figures for a recipe, and
 * its next invoice would have offered a new item. Linked, it is named as the catalog names it.
 */
export function LinkToCatalogDialog({
  open,
  onClose,
  item,
}: {
  open: boolean;
  onClose: () => void;
  item: Item;
}) {
  return (
    <Dialog open={open} onClose={onClose} title="Link to the catalog" wide>
      {open ? <LinkToCatalogForm item={item} onDone={onClose} /> : null}
    </Dialog>
  );
}

function LinkToCatalogForm({ item, onDone }: { item: Item; onDone: () => void }) {
  const notify = useNotify();
  const [typed, setTyped] = useState(item.name);
  const [query, setQuery] = useState(item.name);
  useEffect(() => {
    const t = setTimeout(() => setQuery(typed), 150);
    return () => clearTimeout(t);
  }, [typed]);
  const { data: products = [], isFetching, isError } = useCatalog(query, null);
  const { data: items = [] } = useItems();
  const [picked, setPicked] = useState<CatalogProduct | null>(null);
  // The twin being made one with this item, said in the note once it is.
  const merging = useRef<Item | undefined>(undefined);
  const change = useChange(
    (input: { product: CatalogProduct; twin?: Item }, key) => {
      merging.current = input.twin;
      return send.linkItem(
        { id: item.id, catalogProductId: input.product.id, mergeItemId: input.twin?.id },
        key,
      );
    },
    {
      onSuccess: (linked) => {
        const twin = merging.current;
        notify({
          title: `${item.displayName} is now ${linked.displayName}`,
          body: twin
            ? `What was ${twin.displayName}, its stock, recipes and invoices, is in it now.`
            : undefined,
          link: { kind: "item", itemId: linked.id },
        });
        onDone();
      },
    },
  );

  if (picked) {
    const numbers = picked.offerings.map(offeringLine).join("; ");
    // The product already in inventory as another item, as an invoice adds it when nothing made by
    // hand was linked to it (Tyler, 2026-10-19): linking makes the two one.
    const twin = items.find(
      (i) => i.id !== item.id && !i.archived && picked.adoptedItemIds.includes(i.id),
    );
    return (
      <DialogForm
        onSubmit={() => change.mutate({ product: picked, twin })}
        footer={
          <>
            <button
              type="button"
              className="button"
              onClick={() => {
                change.reset();
                setPicked(null);
              }}
              style={{ marginRight: "auto" }}
            >
              Back
            </button>
            <button type="submit" className="button primary" disabled={change.isPending}>
              {twin ? "Make them one item" : "Link"}
            </button>
          </>
        }
      >
        <p style={{ margin: 0 }}>
          <strong>{item.displayName}</strong> becomes the catalog&rsquo;s{" "}
          <strong>
            {picked.manufacturer ? `${picked.manufacturer} ` : ""}
            {picked.name}
          </strong>
          .
        </p>
        <p className="muted" style={{ margin: 0 }}>
          It takes the catalog&rsquo;s name, maker and specs, and keeps any spec you entered
          yourself. Its lots, pack and history stay as they are.
          {numbers ? ` Invoices with ${numbers} will find it.` : ""}
        </p>
        {twin ? (
          <p className="notice" style={{ margin: 0 }}>
            This product is already in inventory as <strong>{twin.displayName}</strong> (
            {formatQuantity(twin.onHand.quantity, { unit: twin.displayUnit })}), most likely added
            from an invoice. Linking makes them one item: its stock comes here as the same lots, its
            recipes, brews, invoices and item numbers follow, and {twin.displayName} is archived.
          </p>
        ) : null}
        <ErrorNote error={change.error} />
      </DialogForm>
    );
  }

  const searching = query.trim().length >= 2;
  return (
    <DialogForm
      onSubmit={() => products[0] && setPicked(products[0])}
      footer={
        <button type="button" className="button" onClick={onDone}>
          Cancel
        </button>
      }
    >
      <p className="muted" style={{ margin: 0 }}>
        {item.displayName} was added by hand, so the catalog fills in nothing for it. Find the
        product it is.
      </p>
      <div className="search-field">
        <Search size={18} aria-hidden />
        <input
          className="input"
          type="search"
          placeholder="Start typing a name or SKU"
          aria-label="Search the catalog by name or SKU"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          // biome-ignore lint/a11y/noAutofocus: the dialog is the search
          autoFocus
        />
      </div>
      {isError ? <p className="form-error">Search failed. Try again.</p> : null}
      {searching && !isFetching && products.length === 0 ? (
        <p className="muted">
          Nothing matches &ldquo;{query.trim()}&rdquo;. Try fewer words, or the SKU from an invoice.
        </p>
      ) : null}
      {searching && products.length > 0 ? (
        <ul className="suggestions" aria-label="Catalog products" aria-busy={isFetching}>
          {products.map((p) => (
            <ProductRow key={p.id} product={p} onPick={() => setPicked(p)} />
          ))}
        </ul>
      ) : null}
    </DialogForm>
  );
}

/* ---------- Edit specs ---------- */

export function EditSpecsDialog({
  open,
  onClose,
  item,
}: {
  open: boolean;
  onClose: () => void;
  item: Item;
}) {
  return (
    <Dialog open={open} onClose={onClose} title={`Specs for ${item.displayName}`} wide>
      {open ? <EditSpecsForm item={item} onDone={onClose} /> : null}
    </Dialog>
  );
}

function EditSpecsForm({ item, onDone }: { item: Item; onDone: () => void }) {
  const notify = useNotify();
  const { drafts, set } = useSpecDrafts(item.specs, item.category);
  const mainSource = mainSourceOf(item.specs);
  const [specError, setSpecError] = useState<string | null>(null);
  const change = useChange(
    (_: undefined, key) => {
      const changes = collectSpecs(drafts);
      if (changes.error) throw new Error(changes.error);
      return send.patchItem(
        {
          id: item.id,
          patch: {
            specs: changes.specs.length ? changes.specs : undefined,
            removeSpecs: changes.removeSpecs.length ? changes.removeSpecs : undefined,
          },
        },
        key,
      );
    },
    {
      onSuccess: () => {
        notify({
          title: `Specs saved for ${item.displayName}`,
          link: { kind: "item", itemId: item.id },
        });
        onDone();
      },
    },
  );
  return (
    <DialogForm
      onSubmit={() => {
        const changes = collectSpecs(drafts);
        setSpecError(changes.error);
        if (!changes.error) change.mutate(undefined);
      }}
      footer={
        <>
          <button type="button" className="button" onClick={onDone}>
            Cancel
          </button>
          <button type="submit" className="button primary" disabled={change.isPending}>
            Save specs
          </button>
        </>
      }
    >
      <p className="muted" style={{ margin: 0 }}>
        {mainSource ? `Source: ${mainSource}. ` : ""}Modify to set values for your inventory.
      </p>
      <SpecFields drafts={drafts} onChange={set} mainSource={mainSource} />
      {specError ? <div className="form-error">{specError}</div> : null}
      <ErrorNote error={change.error} />
    </DialogForm>
  );
}

/* ---------- Edit lot ---------- */

export function EditLotDialog({
  open,
  onClose,
  item,
  lot,
}: {
  open: boolean;
  onClose: () => void;
  item: Item;
  lot: Lot | null;
}) {
  return (
    <Dialog open={open} onClose={onClose} title={`Lot ${lot?.lotCode ?? ""}`}>
      {open && lot ? <EditLotForm item={item} lot={lot} onDone={onClose} /> : null}
    </Dialog>
  );
}

function EditLotForm({ item, lot, onDone }: { item: Item; lot: Lot; onDone: () => void }) {
  const notify = useNotify();
  const { drafts, set: setDraft } = useSpecDrafts(lot.specs, item.category);
  const mainSource = mainSourceOf(lot.specs);
  const [specError, setSpecError] = useState<string | null>(null);
  const [lotCode, setLotCode] = useState(lot.lotCode ?? "");
  const [cropYear, setCropYear] = useState(lot.cropYear ? String(lot.cropYear) : "");
  const [brand, setBrand] = useState(lot.brand ?? "");
  const [region, setRegion] = useState(lot.region ?? "");
  const [grower, setGrower] = useState(lot.grower ?? "");
  const [bestBy, setBestBy] = useState(lot.bestBy ?? "");
  const [note, setNote] = useState(lot.note ?? "");
  const [priceAmount, setPriceAmount] = useState(lot.price?.amount ?? "");
  const [pricePer, setPricePer] = useState<QuantityUnit>(
    (lot.price?.per as QuantityUnit | undefined) ?? (lot.pack ? "pack" : item.displayUnit),
  );

  const change = useChange(
    (_: undefined, key) => {
      const changes = collectSpecs(drafts);
      if (changes.error) throw new Error(changes.error);
      const patch: LotPatch = {
        lotCode: lotCode.trim() || lot.receivedOn || lot.lotCode,
        cropYear: cropYear ? Number(cropYear) : null,
        // Only when changed, so saving a price does not restate the brand.
        brand: brand.trim() !== (lot.brand ?? "") ? brand.trim() || null : undefined,
        ...(item.category === "hop"
          ? { region: region.trim() || null, grower: grower.trim() || null }
          : {}),
        bestBy: bestBy || null,
        note: note.trim() || null,
        price: isDecimalString(priceAmount)
          ? { amount: priceAmount, per: pricePer }
          : lot.price
            ? null
            : undefined,
        specs: changes.specs.length ? changes.specs : undefined,
        removeSpecs: changes.removeSpecs.length ? changes.removeSpecs : undefined,
      };
      return send.patchLot({ id: lot.id, patch }, key);
    },
    {
      onSuccess: () => {
        notify({ title: "Lot saved", link: { kind: "item", itemId: item.id } });
        onDone();
      },
    },
  );

  return (
    <DialogForm
      onSubmit={() => {
        const changes = collectSpecs(drafts);
        setSpecError(changes.error);
        if (!changes.error) change.mutate(undefined);
      }}
      footer={
        <>
          <button type="button" className="button" onClick={onDone}>
            Cancel
          </button>
          <button type="submit" className="button primary" disabled={change.isPending}>
            Save
          </button>
        </>
      }
    >
      <div className="form-grid">
        <Field label="Lot code">
          <input className="input" value={lotCode} onChange={(e) => setLotCode(e.target.value)} />
        </Field>
        <Field label="Received">
          <input
            className="input"
            value={lot.receivedOn ? showDate(lot.receivedOn) : "Not known"}
            disabled
          />
        </Field>
        {item.category === "hop" ? (
          <Field label="Crop year">
            <input
              className="input num"
              inputMode="numeric"
              value={cropYear}
              onChange={(e) => setCropYear(e.target.value.replace(/\D/g, "").slice(0, 4))}
            />
          </Field>
        ) : null}
        {item.category === "hop" ? (
          <Field label="Brand" hint="The hop company on the bag.">
            <BrandInput value={brand} onChange={setBrand} />
          </Field>
        ) : null}
        {item.category === "hop" ? (
          <Field label="Region" hint="Where they were grown.">
            <RegionInput value={region} onChange={setRegion} />
          </Field>
        ) : null}
        {item.category === "hop" ? (
          <Field label="Grower" hint="The farm that grew them.">
            <input className="input" value={grower} onChange={(e) => setGrower(e.target.value)} />
          </Field>
        ) : null}
        <Field label="Price" hint="As the invoice states it. Clear it to remove the price.">
          <PriceInput
            amount={priceAmount}
            per={pricePer}
            onChange={(amount, per) => {
              setPriceAmount(amount);
              setPricePer(per);
            }}
            dimension={item.dimension as Dimension}
            packName={lot.pack?.name ?? null}
          />
        </Field>
        <Field label="Best by">
          <input
            className="input"
            type="date"
            value={bestBy}
            onChange={(e) => setBestBy(e.target.value)}
          />
        </Field>
        <Field label="Note" className="wide">
          <input className="input" value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
      </div>
      <h3 style={{ margin: "4px 0 0", fontSize: 14 }}>Specs for this lot</h3>
      <p className="muted" style={{ margin: 0 }}>
        {mainSource ? `Source: ${mainSource}. ` : ""}Modify to set lot values. Recipes read these,
        not the item's.
      </p>
      <SpecFields drafts={drafts} onChange={setDraft} mainSource={mainSource} />
      {specError ? <div className="form-error">{specError}</div> : null}
      <ErrorNote error={change.error} />
    </DialogForm>
  );
}
